/**
 * Keeps a focusable line beneath a suggestion card that would otherwise be the
 * last thing in the document.
 *
 * A pending suggestion's preview card is a `Decoration.widget` anchored at the
 * proposal's `to` — an INLINE position inside the anchor block for the ops that
 * target text (`replaceText`, `insertAfterText`). A widget is not a node, so it
 * holds no document position of its own: every click from the anchor text down
 * through the card resolves to that same `to`. When the anchor block is also the
 * document's last block there is nowhere below the card to put a caret, so a
 * clinician cannot click below the suggestion to keep typing — the caret snaps
 * back to the end of the anchor text a frame later (measured live: the browser
 * places a valid DOM caret below the card, then ProseMirror maps it to the only
 * document position there).
 *
 * StarterKit's TrailingNode already solves the same problem for a document
 * ending in a non-paragraph: it appends an empty paragraph so there is somewhere
 * to click. It deliberately does nothing when the last node is already a
 * paragraph — which is exactly the shape a suggestion card creates. This
 * extension extends that same guarantee to "last paragraph carries a card",
 * leaving TrailingNode's own trigger untouched.
 *
 * Deliberately NOT done here: re-anchoring the widget to the end of its
 * enclosing block. That would detach a card from the sentence it replaces
 * whenever the anchor sits mid-paragraph.
 */
import { type Editor, Extension } from '@tiptap/core'
import type { Node as PMNode } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { AI_PROPOSALS_PLUGIN_KEY } from '#src/components/rich-text-editor/AIProposalsExtension.ts'

/*
 * Whether the spare paragraph currently at the end of the document was appended
 * by THIS editor instance in THIS session.
 *
 * Local plugin state, never document state — and load-bearing for safety. The
 * paragraph itself carries no marker (a marker attribute would have to enter the
 * shared Yjs document and the schema), so it is indistinguishable from an empty
 * line the clinician made by pressing Enter at the end of the note. Removing
 * only what we know we added means a blank line we did not create is never
 * deleted, at the cost of leaving one behind across a reload. That trade is
 * deliberate: a stray empty paragraph is cosmetic, deleting a clinician's line
 * is not.
 */
interface TrailingParagraphState {
  inserted: boolean
}

const AI_TRAILING_PARAGRAPH_PLUGIN_KEY = new PluginKey<TrailingParagraphState>(
  'aiSuggestionTrailingParagraph'
)

interface BlockSpan {
  node: PMNode
  start: number
  end: number
}

/*
 * The spare trailing paragraph this extension manages, or null.
 *
 * It is the document's last child, a paragraph, and empty — but NOT when a
 * proposal's own anchor sits inside it. A card painted into an empty last
 * paragraph makes that paragraph the ANCHOR block (it needs a spare line of its
 * own below it), not the spare line. Without that exclusion the predicate would
 * flip on its own output and the insert/remove pair would oscillate.
 */
function findManagedTrailing(
  doc: PMNode,
  anchors: number[]
): { start: number; end: number } | null {
  const last = doc.lastChild
  if (!last || last.type.name !== 'paragraph' || last.content.size !== 0) {
    return null
  }
  const start = doc.content.size - last.nodeSize
  if (anchors.some((to) => to > start && to < doc.content.size)) {
    return null
  }
  return { start, end: doc.content.size }
}

/*
 * The block a card would be painted into: the last child, or the one before it
 * when the spare paragraph is already in place. Reading it relative to the spare
 * line is what makes the "needs a spare line" question STABLE across our own
 * insert — computed against the raw last child it would answer true, then false
 * the moment the paragraph landed.
 */
function findAnchorBlock(
  doc: PMNode,
  trailing: { start: number; end: number } | null
): BlockSpan | null {
  const index = trailing ? doc.childCount - 2 : doc.childCount - 1
  if (index < 0) {
    return null
  }
  let found: BlockSpan | null = null
  doc.forEach((node, offset, childIndex) => {
    if (childIndex === index) {
      found = { node, start: offset, end: offset + node.nodeSize }
    }
  })
  return found
}

/*
 * True when a painted proposal's anchor sits inside a paragraph that is the last
 * real block — the shape whose card has no line beneath it.
 */
function needsTrailingParagraph(
  doc: PMNode,
  anchors: number[],
  trailing: { start: number; end: number } | null
): boolean {
  if (anchors.length === 0) {
    return false
  }
  const anchorBlock = findAnchorBlock(doc, trailing)
  if (!anchorBlock || anchorBlock.node.type.name !== 'paragraph') {
    return false
  }
  return anchors.some((to) => to > anchorBlock.start && to < anchorBlock.end)
}

export const AISuggestionTrailingParagraphExtension = Extension.create({
  name: 'aiSuggestionTrailingParagraph',

  addProseMirrorPlugins() {
    const editor: Editor = this.editor
    return [
      new Plugin<TrailingParagraphState>({
        key: AI_TRAILING_PARAGRAPH_PLUGIN_KEY,

        state: {
          init: () => ({ inserted: false }),
          apply(tr, value) {
            const meta = tr.getMeta(AI_TRAILING_PARAGRAPH_PLUGIN_KEY) as
              | TrailingParagraphState
              | undefined
            if (meta) {
              return meta
            }
            if (!tr.docChanged || !value.inserted) {
              return value
            }
            /* Ownership survives only while the paragraph does. Once the
             * clinician types in it, deletes it, or a peer appends after it, it
             * is no longer ours and the removal branch must not reclaim it. */
            const last = tr.doc.lastChild
            const stillOurs =
              !!last &&
              last.type.name === 'paragraph' &&
              last.content.size === 0
            return stillOurs ? value : { inserted: false }
          },
        },

        appendTransaction(transactions, _oldState, newState) {
          /* A read-only editor is a signed note or an auditor's view: whatever
           * the document ends with is what it ends with. This is also what keeps
           * a note SIGNED with the spare paragraph present from being rewritten
           * behind the clinician. */
          if (!editor.isEditable) {
            return null
          }
          /* The answer can only change when the document changed or the painted
           * set did. Decoration add/remove carries no docChanged, so the
           * proposals meta has to be consulted directly. */
          const relevant = transactions.some(
            (tr) =>
              tr.docChanged || tr.getMeta(AI_PROPOSALS_PLUGIN_KEY) !== undefined
          )
          if (!relevant) {
            return null
          }

          const proposals =
            AI_PROPOSALS_PLUGIN_KEY.getState(newState)?.proposals
          const anchors: number[] = []
          if (proposals) {
            for (const proposal of proposals.values()) {
              anchors.push(proposal.to)
            }
          }

          const doc = newState.doc
          const trailing = findManagedTrailing(doc, anchors)
          const wants = needsTrailingParagraph(doc, anchors, trailing)

          if (wants && !trailing) {
            const paragraph = newState.schema.nodes.paragraph
            if (!paragraph) {
              return null
            }
            const tr = newState.tr.insert(doc.content.size, paragraph.create())
            tr.setMeta(AI_TRAILING_PARAGRAPH_PLUGIN_KEY, { inserted: true })
            // Structural chrome, not an edit the clinician made: keep it out of
            // the plain-ProseMirror undo stack the way a caret move is.
            tr.setMeta('addToHistory', false)
            return tr
          }

          if (!wants && trailing) {
            const owned =
              AI_TRAILING_PARAGRAPH_PLUGIN_KEY.getState(newState)?.inserted ===
              true
            if (!owned) {
              return null
            }
            // Never leave the document empty, and never hand TrailingNode a
            // non-paragraph tail — it would append its own paragraph straight
            // back and the two extensions would trade transactions forever.
            if (doc.childCount < 2) {
              return null
            }
            const previous = doc.child(doc.childCount - 2)
            if (previous.type.name !== 'paragraph') {
              return null
            }
            // The clinician is standing in it.
            const { from, to } = newState.selection
            if (from >= trailing.start && to <= trailing.end) {
              return null
            }
            const tr = newState.tr.delete(trailing.start, trailing.end)
            tr.setMeta(AI_TRAILING_PARAGRAPH_PLUGIN_KEY, { inserted: false })
            tr.setMeta('addToHistory', false)
            return tr
          }

          return null
        },
      }),
    ]
  },
})
